import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Group conversations under a friendly date bucket relative to today.
const dateBucket = (iso) => {
  if (!iso) return 'Earlier';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const daysAgo = Math.floor((startOf(today) - startOf(d)) / 86_400_000);
  if (daysAgo <= 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo < 7) return 'This week';
  if (daysAgo < 30) return 'This month';
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const timeLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const AskSummaros = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/ask/conversations`);
        if (!cancelled) setConversations(r.data?.conversations || []);
      } catch {
        if (!cancelled) setConversations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Group by date bucket, preserving newest-first order.
  const grouped = [];
  const seen = new Map();
  for (const c of conversations) {
    const bucket = dateBucket(c.last_message_at);
    if (!seen.has(bucket)) {
      seen.set(bucket, grouped.length);
      grouped.push({ bucket, items: [] });
    }
    grouped[seen.get(bucket)].items.push(c);
  }

  return (
    <AppShell>
      <SEO title="Ask Summaros" description="Ask Summaros anything about your life." path="/ask" noindex />

      <section className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24 animate-fade-up" data-testid="ask-page">
        <p className="eyebrow mb-5">Ask Summaros</p>
        <h1
          className="display-serif text-4xl sm:text-5xl text-forest text-balance mb-5"
          style={{ fontVariationSettings: "'opsz' 96, 'SOFT' 50, 'wght' 360" }}
          data-testid="ask-title"
        >
          What's on your mind?
        </h1>
        <p className="text-[17px] sm:text-[19px] text-ink-muted leading-[1.65] max-w-xl mb-10 text-pretty" data-testid="ask-body">
          Ask Summaros can read your assessment, profile and timeline, and tell you what it notices. What to focus on. What's shifted. Where you're doing well. What you may be overlooking. Once you've done your Life Assessment, this becomes a proper conversation.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-14">
          <Link to="/assess" className="btn-primary" data-testid="ask-cta-primary">
            Tell me more <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
          <Link to="/my-life" className="btn-secondary" data-testid="ask-cta-secondary">
            Back to My Life
          </Link>
        </div>

        {/* ── Past conversations ─────────────────────── */}
        <div className="pt-10 border-t border-line" data-testid="ask-past">
          <p className="eyebrow mb-6">Past conversations</p>

          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="w-5 h-5 text-forest animate-spin mx-auto" strokeWidth={1.5} />
            </div>
          ) : conversations.length === 0 ? (
            <div
              className="rounded-2xl border border-dashed border-line-strong bg-cream-soft py-10 px-6 text-center"
              data-testid="ask-empty"
            >
              <img
                src="/art/empty-search.png"
                alt=""
                aria-hidden="true"
                draggable={false}
                className="mx-auto mb-4 w-32 h-auto select-none"
                data-testid="ask-empty-art"
              />
              <p className="text-[15px] text-ink mb-1">Nothing here yet.</p>
              <p className="text-[14px] text-ink-muted">Start a conversation when you're ready.</p>
            </div>
          ) : (
            <div className="space-y-10">
              {grouped.map(({ bucket, items }) => (
                <div key={bucket} data-testid={`ask-bucket-${bucket.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-ink-faint mb-3">{bucket}</p>
                  <ul className="divide-y divide-line border border-line rounded-2xl bg-cream-soft overflow-hidden">
                    {items.map((c) => (
                      <li key={c.conversation_id}>
                        <button
                          type="button"
                          className="w-full text-left px-5 py-4 hover:bg-cream-deep transition-colors flex items-center justify-between gap-4"
                          data-testid={`ask-conv-${c.conversation_id}`}
                          // Chat UI lands with the LLM wiring task —
                          // for now the row is a placeholder click.
                        >
                          <div className="min-w-0">
                            <p className="text-[15px] text-forest font-medium leading-snug truncate">{c.title}</p>
                            <p className="text-[12px] text-ink-muted mt-0.5">
                              {timeLabel(c.last_message_at)}
                              {c.message_count > 0 && <span className="ml-2 text-ink-faint">· {c.message_count} messages</span>}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-ink-faint shrink-0" strokeWidth={1.5} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </AppShell>
  );
};

export default AskSummaros;
